import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MetricsCard,
  PageHeader,
  SimpleBarChart,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo } from "react"
import { useDoraMetricsStore, useI18nStore } from "../stores/StoreContext"

export const DoraMetricsPage = observer(() => {
  const doraStore = useDoraMetricsStore()
  const i18nStore = useI18nStore()

  // Initialize store on mount
  useEffect(() => {
    if (!doraStore.hasData && !doraStore.isLoading) {
      doraStore.initialize().catch((error) => {
        console.error("Failed to load DORA metrics", error)
      })
    }
  }, [doraStore])

  // Performance level badge variant
  const getLevelBadge = (level: "elite" | "high" | "medium" | "low") => {
    const variants = {
      elite: "default",
      high: "secondary",
      medium: "outline",
      low: "destructive",
    } as const
    return variants[level]
  }

  // Performance level colors
  const getLevelColor = (level: "elite" | "high" | "medium" | "low") => {
    const colors = {
      elite: "bg-green-500 hover:bg-green-600",
      high: "bg-blue-500 hover:bg-blue-600",
      medium: "bg-yellow-500 hover:bg-yellow-600",
      low: "bg-red-500 hover:bg-red-600",
    }
    return colors[level]
  }

  // Get translated level label
  const getLevelLabel = (level: "elite" | "high" | "medium" | "low") => {
    const keys = {
      elite: "doraLevelElite",
      high: "doraLevelHigh",
      medium: "doraLevelMedium",
      low: "doraLevelLow",
    } as const
    return i18nStore.t(keys[level])
  }

  // Prepare deployment frequency chart data
  const deploymentFrequencyChartData = useMemo(
    () =>
      doraStore.deploymentFrequency.map((d) => ({
        label: d.period,
        value: d.count,
      })),
    [doraStore.deploymentFrequency],
  )

  // Prepare lead time chart data
  const leadTimeChartData = useMemo(
    () =>
      doraStore.leadTimeForChanges.map((d) => ({
        label: d.period,
        value: Math.round(d.averageHours),
      })),
    [doraStore.leadTimeForChanges],
  )

  // Prepare change failure rate chart data
  const changeFailureRateChartData = useMemo(
    () =>
      doraStore.changeFailureRate.map((d) => ({
        label: d.period,
        value: Math.round(d.failureRate * 10) / 10, // Round to 1 decimal
      })),
    [doraStore.changeFailureRate],
  )

  // Prepare time to restore chart data
  const timeToRestoreChartData = useMemo(
    () =>
      doraStore.timeToRestore.map((d) => ({
        label: d.period,
        value: Math.round(d.averageHours),
      })),
    [doraStore.timeToRestore],
  )

  // Loading state
  if (doraStore.isLoading) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" />
            <p className="text-lg">{i18nStore.t("loading")}</p>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (doraStore.loadError) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <Alert variant="destructive">
          <AlertTitle>{i18nStore.t("error")}</AlertTitle>
          <AlertDescription>{doraStore.loadError}</AlertDescription>
        </Alert>
      </div>
    )
  }

  // No data state
  if (!doraStore.hasData) {
    return (
      <div className="container mx-auto py-4 sm:py-6 md:py-8">
        <PageHeader
          title={i18nStore.t("doraMetricsTitle")}
          subtitle={i18nStore.t("doraMetricsSubtitle")}
        />
        <Alert>
          <AlertTitle>{i18nStore.t("doraNoDataTitle")}</AlertTitle>
          <AlertDescription>{i18nStore.t("doraNoDataDescription")}</AlertDescription>
        </Alert>
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{i18nStore.t("doraMissingDataTitle")}</CardTitle>
            <CardDescription>{i18nStore.t("doraMissingDataDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2">
              {doraStore.dataCompleteness.missingDataPoints.map((point) => (
                <li key={point} className="text-sm text-muted-foreground">
                  {i18nStore.t(point)}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    )
  }

  const performanceLevel = doraStore.performanceLevel

  return (
    <div className="container mx-auto py-4 sm:py-6 md:py-8">
      <PageHeader
        title={i18nStore.t("doraMetricsTitle")}
        subtitle={i18nStore.t("doraMetricsSubtitle")}
      />

      {/* Overall Performance Level */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {i18nStore.t("doraOverallPerformance")}
            <Badge
              variant={getLevelBadge(performanceLevel.overall)}
              className={getLevelColor(performanceLevel.overall)}
            >
              {getLevelLabel(performanceLevel.overall)}
            </Badge>
          </CardTitle>
          <CardDescription>{i18nStore.t("doraOverallDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraDeploymentFrequency")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.deploymentFrequency)}
                className={getLevelColor(performanceLevel.deploymentFrequency)}
              >
                {getLevelLabel(performanceLevel.deploymentFrequency)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraLeadTime")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.leadTime)}
                className={getLevelColor(performanceLevel.leadTime)}
              >
                {getLevelLabel(performanceLevel.leadTime)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraChangeFailureRate")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.changeFailureRate)}
                className={getLevelColor(performanceLevel.changeFailureRate)}
              >
                {getLevelLabel(performanceLevel.changeFailureRate)}
              </Badge>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{i18nStore.t("doraTimeToRestore")}</p>
              <Badge
                variant={getLevelBadge(performanceLevel.timeToRestore)}
                className={getLevelColor(performanceLevel.timeToRestore)}
              >
                {getLevelLabel(performanceLevel.timeToRestore)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Completeness Warning */}
      {doraStore.dataCompleteness.missingDataPoints.length > 0 && (
        <Alert className="mb-8">
          <AlertTitle>{i18nStore.t("doraDataLimitationsTitle")}</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{i18nStore.t("doraDataLimitationsDescription")}</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {doraStore.dataCompleteness.missingDataPoints.map((point) => (
                <li key={point}>{i18nStore.t(point)}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Deployment Frequency */}
        {doraStore.dataCompleteness.hasReleases && (
          <MetricsCard
            title={i18nStore.t("doraDeploymentFrequency")}
            description={`${doraStore.averageDeploymentFrequency.perMonth.toFixed(1)} ${i18nStore.t("doraPerMonth")}`}
            percentage={0}
            trend="neutral"
            chartData={deploymentFrequencyChartData.slice(-6)} // Last 6 months
            chartType="bar"
            distributionLabel={i18nStore.t("doraDeploymentsByMonth")}
          />
        )}

        {/* Lead Time for Changes */}
        {doraStore.dataCompleteness.hasPullRequests && (
          <MetricsCard
            title={i18nStore.t("doraLeadTime")}
            description={`${doraStore.averageLeadTime.days.toFixed(1)} ${i18nStore.t("doraDays")}`}
            percentage={0}
            trend="neutral"
            chartData={leadTimeChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraLeadTimeTrend")}
          />
        )}

        {/* Change Failure Rate */}
        {doraStore.dataCompleteness.hasReleases && doraStore.dataCompleteness.hasCommits && (
          <MetricsCard
            title={i18nStore.t("doraChangeFailureRate")}
            description={`${doraStore.averageChangeFailureRate.toFixed(1)}%`}
            percentage={0}
            trend="neutral"
            chartData={changeFailureRateChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraFailureRateTrend")}
          />
        )}

        {/* Time to Restore Service */}
        {doraStore.dataCompleteness.hasCommits && (
          <MetricsCard
            title={i18nStore.t("doraTimeToRestore")}
            description={`${doraStore.averageTimeToRestore.hours.toFixed(1)} ${i18nStore.t("doraHours")}`}
            percentage={0}
            trend="neutral"
            chartData={timeToRestoreChartData.slice(-6)} // Last 6 months
            chartType="line"
            trendLabel={i18nStore.t("doraRestoreTimeTrend")}
          />
        )}
      </div>

      {/* Detailed Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Deployment Frequency Detail */}
        {doraStore.dataCompleteness.hasReleases && deploymentFrequencyChartData.length > 0 && (
          <SimpleBarChart
            title={i18nStore.t("doraDeploymentFrequencyDetail")}
            description={i18nStore.t("doraDeploymentFrequencyDetailDescription")}
            data={deploymentFrequencyChartData}
          />
        )}

        {/* Lead Time Detail */}
        {doraStore.dataCompleteness.hasPullRequests && leadTimeChartData.length > 0 && (
          <SimpleBarChart
            title={i18nStore.t("doraLeadTimeDetail")}
            description={i18nStore.t("doraLeadTimeDetailDescription")}
            data={leadTimeChartData}
          />
        )}
      </div>

      {/* Methodology Note */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{i18nStore.t("doraMethodologyTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-1">
              {i18nStore.t("doraDeploymentFrequency")}:
            </p>
            <p>{i18nStore.t("doraDeploymentFrequencyMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">{i18nStore.t("doraLeadTime")}:</p>
            <p>{i18nStore.t("doraLeadTimeMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">
              {i18nStore.t("doraChangeFailureRate")}:
            </p>
            <p>{i18nStore.t("doraChangeFailureRateMethodology")}</p>
          </div>
          <div>
            <p className="font-medium text-foreground mb-1">{i18nStore.t("doraTimeToRestore")}:</p>
            <p>{i18nStore.t("doraTimeToRestoreMethodology")}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
})
